import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { CachedFile, Image, Notebook, Page, StoredStroke, TextBox } from './types';
import type { OutboxEntry } from '../sync/protocol';

export interface CoteDB extends DBSchema {
  notebooks: { key: string; value: Notebook };
  pages: { key: string; value: Page; indexes: { byNotebook: string } };
  strokes: { key: string; value: StoredStroke; indexes: { byPage: string } };
  /** Typed text boxes (M3b). */
  textboxes: { key: string; value: TextBox; indexes: { byPage: string } };
  /** Placed images (M4): placement rects; pixel bytes live in `files`. */
  images: { key: string; value: Image; indexes: { byPage: string } };
  /** R2 file cache (M4): fileId → cached bytes, LRU-by-size (Repo.putFile). */
  files: { key: string; value: CachedFile };
  meta: { key: string; value: unknown };
  /** Sync journal: every local mutation, drained by the sync engine (M2). */
  outbox: { key: number; value: OutboxEntry };
  /** Sync k/v state: lastServerSeq, deviceId, syncToken, syncEnabled. */
  syncmeta: { key: string; value: unknown };
}

export type CoteDatabase = IDBPDatabase<CoteDB>;

export function openCoteDB(): Promise<CoteDatabase> {
  return openDB<CoteDB>('cote', 4, {
    // Fallthrough switch: each case upgrades from that oldVersion, so any
    // starting version replays exactly the missing steps.
    upgrade(db, oldVersion) {
      switch (oldVersion) {
        case 0: {
          db.createObjectStore('notebooks', { keyPath: 'id' });
          const pages = db.createObjectStore('pages', { keyPath: 'id' });
          pages.createIndex('byNotebook', 'notebookId');
          const strokes = db.createObjectStore('strokes', { keyPath: 'id' });
          strokes.createIndex('byPage', 'pageId');
          db.createObjectStore('meta');
        }
        // falls through
        case 1: {
          db.createObjectStore('outbox', { autoIncrement: true });
          db.createObjectStore('syncmeta');
        }
        // falls through
        case 2: {
          const textboxes = db.createObjectStore('textboxes', { keyPath: 'id' });
          textboxes.createIndex('byPage', 'pageId');
        }
        // falls through
        case 3: {
          const images = db.createObjectStore('images', { keyPath: 'id' });
          images.createIndex('byPage', 'pageId');
          db.createObjectStore('files'); // out-of-line key: fileId
        }
      }
    },
    // This connection is blocking another tab/context's upgrade or a
    // deleteDatabase() call (e.g. test teardown re-opening a fresh DB).
    // Close it so the other request can proceed instead of hanging forever.
    blocking(_currentVersion, _blockedVersion, event) {
      (event.target as IDBDatabase).close();
    },
  });
}
